import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import BaseTable from "@components/DataTable/BaseTable";
import { formatDateTime } from "@features/vessel-call/grid/utils.ts";
import { mapMovementServiceStatus } from "@features/vessel-call/vessel-call-status/MovementServices/preview/movementServiceStatus.mapper.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import type { Movement } from "@services/movement-services/types";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import AddMovementServiceButton from "../components/AddMovementServiceButton";
import MovementFlowButtons from "../flow/MovementFlowButtons";
import { canUserUpdate } from "../flow/utils";
import { UPDATE_MOVEMENT_CONFIG } from "../form/updateMovement/utils";
import type { MovementOutletContext } from "../MovementServicesContainer";
import QuickCaptureTimePickers from "./QuickCaptureTimePickers";

interface CurrentMovementsProps {
  langBase: string;
  rows?: Movement[];
  headers: { field: string; columnName: string; minWidth?: number }[];
  onRowClick?: (row: Movement) => void;
  openDrawer: (drawerName: string) => void;
}

const CurrentMovements: React.FC<CurrentMovementsProps> = ({
  langBase,
  rows = [],
  headers,
  openDrawer,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { fakeStatus, ownersMatter, proposedAgency, currentMovements } =
    useOutletContext<MovementOutletContext>();
  const { userAccessPermissions } = useAuthStore();
  const currentMovement = currentMovements?.[0];
  const isMarineDepartment = checkPermission(userAccessPermissions, "hasTaskAccess");
  const isQuickCaptureStatus = ["APPROVED", "MOVEMENT_STARTED"].includes(
    currentMovement?.status ?? "",
  );
  const canQuickCapture = Boolean(
    currentMovement?.task &&
      canUserUpdate(
        currentMovement.task.status,
        currentMovement.task.claimOwnerName,
        currentMovement.status,
      ),
  );
  const quickCaptureConfig = currentMovement?.movementType
    ? UPDATE_MOVEMENT_CONFIG[currentMovement.movementType]
    : undefined;

  let movementFlowData;
  if (rows.length) {
    const { id, status, task, terminalReport, to, from, submittedByRole } = rows[0];
    // Use fakeStatus if available, otherwise use actual status
    const effectiveStatus = fakeStatus || status;
    movementFlowData = {
      id,
      status: effectiveStatus,
      submittedByRole,
      task,
      terminalReport,
      to,
      from,
    };
  }

  const mappedRows = rows.map((row) => {
    // Use fakeStatus if available, otherwise use actual status
    const effectiveStatus = fakeStatus || row.status;
    const statusMapping = mapMovementServiceStatus(String(effectiveStatus).toUpperCase());

    return {
      ...row,
      ats: row.ats ? formatDateTime(String(row.ats), "-") : "-",
      etc: row.etc ? formatDateTime(String(row.etc), "-") : "-",
      ets: row.ets ? formatDateTime(String(row.ets), "-") : "-",
      rta: row.rta ? formatDateTime(String(row.rta), "-") : "-",
      rtd: row.rtd ? formatDateTime(String(row.rtd), "-") : "-",
      rts: row.rts ? formatDateTime(String(row.rts), "-") : "-",
      badgeType: statusMapping.badgeType,
      status: t(statusMapping.translationKey, {
        defaultValue: effectiveStatus,
      }),
    };
  });

  return rows.length ? (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography
          data-testid="table-title"
          variant="h6"
          sx={{ fontWeight: theme.typography.fontWeightLight }}
        >
          {t(`${langBase}.current-movement-title`)}
        </Typography>
      </Box>

      <BaseTable
        rows={mappedRows}
        headers={headers}
        onRowClick={(row) => {
          navigate(`${row.id}`);
        }}
      />
      {isMarineDepartment && isQuickCaptureStatus && currentMovement && quickCaptureConfig && (
        <QuickCaptureTimePickers
          movement={currentMovement}
          config={quickCaptureConfig}
          canCapture={canQuickCapture}
        />
      )}
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <MovementFlowButtons openDrawer={openDrawer} movementFlowData={movementFlowData} />
      </Box>
    </>
  ) : (
    <div className="w-full flex justify-end">
      <AddMovementServiceButton
        langBase={langBase}
        ownersMatter={ownersMatter}
        proposedAgency={proposedAgency}
      />
    </div>
  );
};

export default CurrentMovements;
